import type { ModuleEndpoint, ModuleRegistryRow, ModuleSnapshot } from '@ctcells/contract'
import { moduleTypeLabel, t } from '@ctcells/i18n'

/**
 * 실물 `ModuleSnapshot` → 모듈 표 한 줄.
 *
 * 🔴 **응답에 있는 값으로만 표를 짠다**(2026-09-20 eric 지시 — 「저 응답에 맞게 화면을 구성해」).
 *    없는 값을 열로 세워 두고 전부 `—` 를 찍던 것을 걷어냈다.
 *
 * 2026-09-20 실서버 `/openapi.json` 의 `ModuleSnapshot` 기준, 표가 쓰는 값의 출처는 이렇다.
 *
 * | 열 | 출처 | 비고 |
 * | --- | --- | --- |
 * | 이름 | **`display_name` 이 있으면 그것**, 없으면 `module_type` 원값 → 화면이 한글 라벨로 | 사전에 없는 종류(`ceiling_3axis`)는 `—` · 2026-09-26 실측 8대 전부 `display_name: null` |
 * | 모듈 ID | `module_id` | **같은 종류가 여럿일 때 구분할 유일한 값** |
 * | 상태 | `state` | 원값 그대로 나르고 표는 **굵은 표기**로 접는다(`coarseModuleState.ts`) · 세부는 툴팁·상세 |
 * | 연결 | `stale` | 서버 판정값 |
 * | 마지막 응답 | `last_seen` | ISO 원문 → 화면이 **시각**으로 찍는다(상대 시간 아님) |
 * | 제어 소유 | `control_owner` | `orchestrator` \| `local` |
 * | 알람 | `active_alarm_count` | 0 이면 `—` |
 * | 안전 | `e_stop` · `interlock.state` | 한 열로 합쳤다 — 아래 이유 |
 *
 * **왜 안전을 한 열로 합쳤나.** 둘 다 「지금 이 모듈을 만질 수 있는가」 한 가지를 말하고,
 * 평상시에는 **둘 다 정상**이라 열을 나누면 표의 절반이 「정상 / 닫힘」으로 채워진다.
 * 합치면 **비정상일 때만 글자가 바뀐다** — 표에서 눈에 걸려야 할 것이 걸린다.
 *
 * **뺀 열**(2026-09-20): `IP 주소` · `응답 확인` · `최근 진단` · `보정` · `버전`.
 * 앞의 넷은 **응답·계약 어디에도 없고**, 「보정」은 `/calibrations` 가 0건이라 늘 비어 있었다.
 * ⚠ 모듈 상세의 「보정」 탭도 2026-10-22 에 걷었다(eric 「보정과 시스템 로그 탭은 제거해줘..」).
 * 주소는 값이 실재하지만(`GET /modules/{id}/endpoint`) **모듈마다 따로 불러야 해서**
 * 목록에서 N+1 을 지게 된다 → **상세에서만 부른다.**
 *
 * **상세만 쓰는 값**(2026-09-26 새 응답 칸): `model` · `fw_version`(연결 카드). 표에는 열을 만들지 않았다.
 * 🔴 미러 넷(`chamber_thermal` · `shaker_latch` · `tray_latch` · `incubation`)은 이 줄에 싣지 않는다 — 모듈 상세의 구성 요소 카드를
 *    걷었다(eric 2026-10-22 「모니터링 화면에 있으면 되는 거라」). 모니터링은 `ModuleSnapshot` 을 직접 읽는다.
 * ⚠ `devices` · `handoff_occupancy` 는 아직 화면에 안 싣는다(팀장 지시 · 모양만 보고).
 *
 * **폐기(`service_state: 'terminated'`) 모듈은 표에서 뺀다** — eric 2026-09-20 「active 만 보이게」
 * (`ModulesContainer` · `moduleService.ts`). 판정은 `=== 'terminated'` 하나다 — 칸이 없으면 감추지 않는다.
 *
 * 🔴 **이 표가 이 화면의 진실이다.** 열을 더하거나 뺄 때 여기를 같이 고쳐라.
 */

/*
 * 🔴 **`module_type` → 한글 라벨 표는 `@ctcells/i18n` 에 하나만 둔다** (2026-09-20 합침).
 *    여기에 같은 표(`TYPE_TO_MODULE_ID`)가 한 벌 더 있었다 — 표가 둘이면 `ceiling_3axis` 같은
 *    종류가 늘 때 한쪽만 고쳐지고 화면마다 다른 이름이 뜬다.
 * 🔴 **이 함수는 라벨을 만들지 «않는다»**(2026-09-21). 종전에는 여기서 `moduleTypeLabel()` 을
 *    불러 한글을 `display_name` 에 구워 넣었는데, 모르는 종류를 `—` 로 접어 버려서 **제목이
 *    「— · centrifuge-1」** 이 될 판이었다. 처분은 부르는 쪽마다 다르다 — 원값만 나른다.
 *
 * 🔴 **`model` 은 이름이 아니다**(2026-09-26 응답에 생겼다). 실측 값이 `null`(실모듈 넷) 또는
 *    `module_type` 과 같은 문자열(`mock-*` 넷)이라 이름 칸에 쓰면 원값 영문이 선다. 이름 칸은
 *    **`display_name` → `module_type` 라벨** 순이고, `model` 은 상세 연결 카드의 한 줄로 간다.
 *
 * ⚠ **이 라벨만으로는 같은 종류 2대를 구분할 수 없다.** 실제로 그런 상태다 —
 *   2026-09-20 실측으로 `/modules` 가 **8건**(살아 있는 4 + 폐기된 4)을 주고 종류마다 2대씩이다.
 *   그래서 표에 **「모듈 ID」 열을 따로 둔다** — 구분되는 값이 그것뿐이다.
 *
 * ⚠ `moduleTypeLabel()` 은 모르는 종류에 **`undefined`** 를 낸다(`—` 가 아니다) — 처분이
 *   부르는 쪽마다 다르기 때문이다. **목록 표는 `—`, 상세 제목은 `module_id`** 로 떨어뜨린다.
 */
/** 주소를 못 받았을 때. 화면에는 `—` 로 나간다 */
const NO_HOST = ''

/**
 * @param endpoint 이 모듈의 주소. **목록은 넘기지 않는다**(N+1 을 지지 않으려고) —
 *   상세에서만 채운다. 못 받았으면 그 행의 주소만 `—` 가 되고 나머지 값은 그대로 산다.
 */
export function toRegistryRow(
  snapshot: ModuleSnapshot,
  endpoint?: ModuleEndpoint,
): ModuleRegistryRow {
  return {
    module_id: snapshot.module_id,
    // 🔴 원값이다. 없으면 `null` — 서버가 `module_id` 로 채우지 않는다(계약). 대체는 화면이 한다
    display_name: nonEmpty(snapshot.display_name),
    model: nonEmpty(snapshot.model),
    fw_version: nonEmpty(snapshot.fw_version),
    // 🔴 원값을 그대로 나른다. 한글로 바꾸는 일은 화면이 한다(`moduleTypeLabel`)
    module_type: snapshot.module_type,
    // 🔴 원값을 그대로 나른다. 한글로 바꾸는 일은 화면(`enumLabel`)이 한다
    state: snapshot.state,
    stale: snapshot.stale,
    last_seen: snapshot.last_seen ?? null,
    control_owner: snapshot.control_owner,
    active_alarm_count: snapshot.active_alarm_count ?? null,
    // ⚠ `interlock` 자체가 미발행일 수 있다. 그때는 「닫힘」이 아니라 **모름**이다
    interlock_state: snapshot.interlock?.state ?? null,
    e_stop: snapshot.e_stop ?? null,
    host: endpoint?.host ?? NO_HOST,
    port: endpoint?.port ?? 0,
    address_source: endpoint?.address_source ?? 'manual',
  }
}

/** 빈 글자는 `null` — 서버가 칸을 비워 보내도 「값 있음」으로 읽지 않는다 */
const nonEmpty = (value: string | null | undefined): string | null =>
  value == null || value === '' ? null : value

/**
 * 표의 이름 칸. 🔴 **`display_name` → 종류 라벨 → `—`** 순이다(표 한 칸이라 비울 수 없다).
 * ⚠ `model` 은 이름으로 쓰지 않는다 — 레지스트리의 모델명이고 실측 값이 영문 원값이다.
 */
export function moduleName(m: Pick<ModuleRegistryRow, 'display_name' | 'module_type'>): string {
  return m.display_name ?? moduleTypeLabel(m.module_type) ?? t('mask.none')
}
